import "./styles/main.css";
import { Navbar } from "./components/layout/Navbar.js";
import { Footer } from "./components/layout/Footer.js";
import { HomePage } from "./pages/home.js";
import { ProductPage } from "./pages/product.js";
import { RequestReceivedPage } from "./pages/requestReceived.js";
import { resolveRoute, bindRouter, updatePageMeta } from "./routes/router.js";
import { initContactForm } from "./components/marketing/ContactForm.js";

const app = document.querySelector("#app");
let motionCleanup = () => {};
let navigationCleanup = () => {};

function NotFoundPage() {
  return `<main id="contenido"><section class="not-found"><div class="site-container"><span class="eyebrow">Error 404</span><h1>No encontramos esta página.</h1><p>La dirección puede haber cambiado o no existir.</p><a class="button button-primary" href="/" data-route>Regresar al inicio</a></div></section></main>`;
}

function initNavigation() {
  navigationCleanup();
  const controller = new AbortController();
  const toggle = document.querySelector("[data-nav-toggle]");
  const nav = document.querySelector("[data-nav]");
  const dropdownToggle = document.querySelector("[data-dropdown-toggle]");
  const dropdown = document.querySelector("[data-dropdown]");

  const closeProducts = () => {
    dropdown?.classList.remove("is-open");
    dropdownToggle?.setAttribute("aria-expanded", "false");
  };

  toggle?.addEventListener("click", () => {
    const open = nav?.classList.toggle("is-open") ?? false;
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "Cerrar menú" : "Abrir menú");
  }, { signal: controller.signal });
  dropdownToggle?.addEventListener("click", () => {
    const open = dropdown?.classList.toggle("is-open") ?? false;
    dropdownToggle.setAttribute("aria-expanded", String(open));
  }, { signal: controller.signal });
  document.querySelectorAll("[data-nav] a").forEach((link) => link.addEventListener("click", () => {
    nav?.classList.remove("is-open");
    toggle?.setAttribute("aria-expanded", "false");
    closeProducts();
  }, { signal: controller.signal }));
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element) || dropdown?.contains(event.target)) return;
    closeProducts();
  }, { signal: controller.signal });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    closeProducts();
      if (dropdownToggle instanceof HTMLElement) {
        dropdownToggle.focus();
      }
  }, { signal: controller.signal });

  navigationCleanup = () => controller.abort();
}

function scrollToRequestedSection() {
  const hash = window.location.hash;
  if (!hash) {
    window.scrollTo({ top: 0, behavior: "instant" });
    return;
  }
  window.requestAnimationFrame(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }));
}

function initMotion() {
  const progress = document.querySelector("[data-scroll-progress]");
  const header = document.querySelector("[data-header]");
  const heroVisual = document.querySelector(".hero-visual");
  const heroOrbs = document.querySelectorAll(".hero-orb");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let animationFrame = 0;

  const updateScrollEffects = () => {
    const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
    const progressValue = Math.min((window.scrollY / scrollable) * 100, 100);
    if (progress instanceof HTMLElement) progress.style.width = `${progressValue}%`;
    header?.classList.toggle("is-scrolled", window.scrollY > 18);

    if (!reduceMotion && heroVisual instanceof HTMLElement) {
      const shift = Math.min(window.scrollY * 0.085, 48);
      heroVisual.style.setProperty("--hero-shift", `${shift}px`);
      heroOrbs.forEach((orb, index) => {
        if (orb instanceof HTMLElement) orb.style.setProperty("--orb-shift", `${Math.min(window.scrollY * (index ? -0.035 : 0.045), 30)}px`);
      });
    }
    animationFrame = 0;
  };

  const onScroll = () => {
    if (!animationFrame) animationFrame = window.requestAnimationFrame(updateScrollEffects);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  updateScrollEffects();

  /** @type {IntersectionObserver | null} */
  let observer = null;
  if (!reduceMotion) {
    document.documentElement.classList.add("motion-ready");
    const revealElements = document.querySelectorAll(
      ".section-heading-row, .ecosystem-section .mx-auto, .product-card, .needs-list article, .reason-grid article, .process-grid li, .service-grid article, .about-grid > *, .contact-grid > *, .product-details-grid > *, .feature-grid article, .preview-section-heading, .product-wide-preview, .cta-panel",
    );
    revealElements.forEach((element, index) => {
      if (!(element instanceof HTMLElement)) return;
      element.classList.add("reveal-item");
      element.style.setProperty("--reveal-delay", `${(index % 6) * 55}ms`);
    });
    observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer?.unobserve(entry.target);
      }),
      { threshold: 0.12, rootMargin: "0px 0px -7%" },
    );
    revealElements.forEach((element) => observer?.observe(element));
  }

  motionCleanup = () => {
    window.removeEventListener("scroll", onScroll);
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    observer?.disconnect();
    document.documentElement.classList.remove("motion-ready");
  };
}

export function render() {
  if (!app) return;
  motionCleanup();
  navigationCleanup();
  const route = resolveRoute(window.location.pathname);
  const content = route.type === "home"
    ? HomePage()
    : route.type === "product"
      ? ProductPage(route.product)
      : route.type === "request-received"
        ? RequestReceivedPage()
        : NotFoundPage();
  app.innerHTML = `<div class="scroll-progress" aria-hidden="true"><span data-scroll-progress></span></div>${Navbar()}${content}${Footer()}`;
  updatePageMeta();
  initNavigation();
  initContactForm();
  initMotion();
  scrollToRequestedSection();
}

bindRouter(render);
render();
